import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { theme } from './theme';
import { ToastProvider } from './contexts/ToastContext';
import { UserProvider } from './contexts/UserContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { loginRequest } from './authConfig';
import Layout from './components/layout/Layout';
import AvailablePrograms from './components/submissions/AvailablePrograms';
import SubmissionsList from './components/submissions/SubmissionsList';
import CreateSubmission from './components/submissions/CreateSubmission';
import SubmissionDetail from './components/submissions/SubmissionDetail';
import ManagerReview from './components/manager/ManagerReview';
import AdminReview from './components/admin/AdminReview';
import ProgramManagement from './components/admin/ProgramManagement';
import UserManagement from './components/admin/UserManagement';
import SubmissionReport from './components/admin/SubmissionReport';
import RoleBasedRedirect from './components/common/RoleBasedRedirect';

// Auto-login wrapper - redirects unauthenticated users to Microsoft login automatically
const AutoLoginWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    // If user is not authenticated and MSAL is not currently processing, redirect to login
    if (!isAuthenticated && !isLoading && inProgress === 'none') {
      console.log('User not authenticated, redirecting to Microsoft login...');
      instance.loginRedirect(loginRequest).catch((error) => {
        console.error('Auto-login redirect failed:', error);
      });
    }
  }, [isAuthenticated, isLoading, inProgress, instance]);

  // Show loading spinner while checking authentication or redirecting
  if (isLoading || (!isAuthenticated && inProgress !== 'none')) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
          {inProgress === 'login' ? 'Redirecting to Microsoft Login...' : 'Loading...'}
        </Typography>
      </Box>
    );
  }

  // Show loading while triggering redirect
  if (!isAuthenticated) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary' }}>
          Redirecting to Microsoft Login...
        </Typography>
      </Box>
    );
  }

  // User is authenticated, render the app
  return <>{children}</>;
};

// Protected Route Component - simplified since AutoLoginWrapper handles auth
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    // This shouldn't happen with AutoLoginWrapper, but keeping as fallback
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Redirecting to login...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

// App Routes Component (needs to be inside AuthProvider)
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Redirect /login to home - we auto-login now */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      
      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <RoleBasedRedirect />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/programs"
        element={
          <ProtectedRoute>
            <Layout>
              <AvailablePrograms />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/submissions"
        element={
          <ProtectedRoute>
            <Layout>
              <SubmissionsList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/submissions/create"
        element={
          <ProtectedRoute>
            <Layout>
              <CreateSubmission />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/submissions/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SubmissionDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/review"
        element={
          <ProtectedRoute>
            <Layout>
              <ManagerReview />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/review"
        element={
          <ProtectedRoute>
            <Layout>
              <AdminReview />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/programs"
        element={
          <ProtectedRoute>
            <Layout>
              <ProgramManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <Layout>
              <UserManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <SubmissionReport />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <AuthProvider>
          <UserProvider>
            <AutoLoginWrapper>
              <AppRoutes />
            </AutoLoginWrapper>
          </UserProvider>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;